import {Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {AccountingOffice, AccountingOfficeApiService, User} from 'patja-api';
import {catchError, Observable, of, tap} from 'rxjs';
import {NzConfigService} from 'ng-zorro-antd/core/config';

export const PLATTA_PRIMARY_COLOR = '#E87A00';

@Injectable({
  providedIn: 'root',
})
export class AccountingOfficeThemeService {
  private accountingOffice: WritableSignal<AccountingOffice | null> = signal(null);

  constructor(
    private accountingOfficeApiService: AccountingOfficeApiService,
    private nzConfigService: NzConfigService,
  ) {}

  getAccountingOffice(): Signal<AccountingOffice | null> {
    return this.accountingOffice.asReadonly();
  }

  applyForUser(user: User | null): Observable<AccountingOffice | null> {
    const officeId = this.resolveOfficeId(user);
    if (!officeId) {
      this.resetToDefault();
      return of(null);
    }

    const cached = this.accountingOffice();
    if (cached?.id === officeId) {
      this.applyTheme(cached);
      return of(cached);
    }

    return this.accountingOfficeApiService.getAccountingOfficeById(officeId).pipe(
      tap((office: AccountingOffice) => this.applyTheme(office)),
      catchError(() => {
        this.resetToDefault();
        return of(null);
      }),
    );
  }

  applyIfCurrentOffice(office: AccountingOffice): void {
    if (this.accountingOffice()?.id === office.id) {
      this.applyTheme(office);
    }
  }

  resetToDefault(): void {
    this.nzConfigService.set('theme', {primaryColor: PLATTA_PRIMARY_COLOR});
    this.accountingOffice.set(null);
  }

  private applyTheme(office: AccountingOffice): void {
    if (office.primaryColor) {
      this.nzConfigService.set('theme', {primaryColor: office.primaryColor});
    }
    this.accountingOffice.set(office);
  }

  private resolveOfficeId(user: User | null): string | null {
    if (!user || user.isSuperAdmin() || !user.isAccountingOfficeAdmin()) {
      return null;
    }

    return (
      user.activeAccountingOfficeId ||
      user.accountingOfficeId ||
      user.accountingOfficeIds?.[0] ||
      null
    );
  }
}
